import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Chip,
  useTheme,
  alpha,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Tooltip,
  InputAdornment,
  Fade,
  Alert,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  School as SchoolIcon,
  Psychology as PsychologyIcon,
  Work as WorkIcon,
  Translate as TranslateIcon,
  Description as DescriptionIcon,
  Help as HelpIcon,
  Verified as VerifiedIcon,
  LocalHospital as HospitalIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { professionalDetailsSchema, validateField } from '../../../utils/validationSchemas';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(8px)',
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    opacity: 0.8,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    transition: 'all 0.2s ease-in-out',
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
    '&:hover fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.4),
    },
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    transition: 'all 0.2s ease-in-out',
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
    '&:hover fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.4),
    },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    padding: '16.5px 14px',
    transition: 'all 0.2s ease-in-out',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.primary.main, 0.2),
    transition: 'all 0.2s ease-in-out',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.primary.main, 0.4),
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

const doctorTypes = [
  {
    value: 'psychiatrist',
    label: 'Psychiatrist',
    description: 'Medical doctor specializing in mental health, can prescribe medications',
    icon: <HospitalIcon />,
    qualifications: [
      'MBBS',
      'MD Psychiatry',
      'DNB Psychiatry',
      'MRCPsych',
      'DPM',
      'M.D. Psychiatry',
      'M.S. Psychiatry',
      'MCh Psychiatry',
      'DM Psychiatry',
      'Fellowship in Psychiatry',
      'Diploma in Psychological Medicine',
      'Diploma in Psychiatry',
      'Post Graduate Diploma in Psychiatry',
    ],
    specializations: [
      'General Psychiatry',
      'Child & Adolescent Psychiatry',
      'Geriatric Psychiatry',
      'Addiction Psychiatry',
      'Forensic Psychiatry',
      'Consultation-Liaison Psychiatry',
      'Psychosomatic Medicine',
      'Community Psychiatry',
      'Emergency Psychiatry',
      'Military Psychiatry',
      'Psychiatric Rehabilitation',
      'Psychiatric Research',
      'Psychiatric Education',
      'Psychiatric Administration',
      'Psychiatric Epidemiology',
    ],
    experienceOptions: [
      '0-2 years',
      '2-5 years',
      '5-10 years',
      '10-15 years',
      '15-20 years',
      '20+ years',
    ],
    bioPlaceholder: 'Describe your approach to psychiatric care, treatment philosophies, and areas of expertise...',
  },
  {
    value: 'clinical_psychologist',
    label: 'Clinical Psychologist',
    description: 'Doctorate-level psychologist specializing in psychological assessment and therapy',
    icon: <PsychologyIcon />,
    qualifications: [
      'M.Phil Clinical Psychology',
      'PhD Clinical Psychology',
      'PsyD',
      'MSc Clinical Psychology',
      'MA Clinical Psychology',
      'M.Phil Rehabilitation Psychology',
      'M.Phil Neuropsychology',
      'M.Phil Health Psychology',
      'M.Phil Child Psychology',
      'M.Phil Adult Psychology',
      'M.Phil Geriatric Psychology',
      'M.Phil Sports Psychology',
      'M.Phil Industrial Psychology',
      'M.Phil Educational Psychology',
      'M.Phil Forensic Psychology',
    ],
    specializations: [
      'Clinical Psychology',
      'Neuropsychology',
      'Health Psychology',
      'Child Psychology',
      'Adult Psychology',
      'Geriatric Psychology',
      'Rehabilitation Psychology',
      'Sports Psychology',
      'Industrial Psychology',
      'Educational Psychology',
      'Forensic Psychology',
      'Clinical Neuropsychology',
      'Clinical Health Psychology',
      'Clinical Child Psychology',
      'Clinical Adult Psychology',
    ],
    experienceOptions: [
      '0-2 years',
      '2-5 years',
      '5-10 years',
      '10-15 years',
      '15-20 years',
      '20+ years',
    ],
    bioPlaceholder: 'Describe your therapeutic approach, assessment methods, and areas of expertise in psychological care...',
  },
  {
    value: 'counselor',
    label: 'Counselor/Therapist',
    description: 'Mental health professional providing counseling and therapy services',
    icon: <PsychologyIcon />,
    qualifications: [
      'MA Counseling Psychology',
      'MSc Counseling Psychology',
      'MA Clinical Psychology',
      'MSc Clinical Psychology',
      'MA Applied Psychology',
      'MSc Applied Psychology',
      'MA Psychology',
      'MSc Psychology',
      'M.Phil Counseling Psychology',
      'M.Phil Applied Psychology',
      'M.Phil Psychology',
      'Diploma in Counseling',
      'Advanced Diploma in Counseling',
      'Post Graduate Diploma in Counseling',
      'Certificate in Counseling',
    ],
    specializations: [
      'Individual Counseling',
      'Family Therapy',
      'Marriage Counseling',
      'Career Counseling',
      'Addiction Counseling',
      'Trauma Counseling',
      'Grief Counseling',
      'Group Therapy',
      'Behavioral Therapy',
      'Cognitive Therapy',
      'Psychodynamic Therapy',
      'Humanistic Therapy',
      'Integrative Therapy',
      'Solution-Focused Therapy',
      'Narrative Therapy',
    ],
    experienceOptions: [
      '0-2 years',
      '2-5 years',
      '5-10 years',
      '10-15 years',
      '15-20 years',
      '20+ years',
    ],
    bioPlaceholder: 'Describe your counseling approach, therapeutic techniques, and areas of expertise...',
  },
  {
    value: 'psychiatric_nurse',
    label: 'Psychiatric Nurse',
    description: 'Registered nurse specializing in mental health care',
    icon: <HospitalIcon />,
    qualifications: [
      'BSc Nursing',
      'MSc Psychiatric Nursing',
      'MSc Mental Health Nursing',
      'Post Basic BSc Nursing',
      'Diploma in Psychiatric Nursing',
      'Advanced Diploma in Psychiatric Nursing',
      'Post Graduate Diploma in Psychiatric Nursing',
      'Certificate in Psychiatric Nursing',
      'M.Phil Psychiatric Nursing',
      'PhD Psychiatric Nursing',
      'Diploma in Mental Health Nursing',
      'Advanced Diploma in Mental Health Nursing',
      'Post Graduate Diploma in Mental Health Nursing',
      'Certificate in Mental Health Nursing',
      'Specialized Training in Psychiatric Nursing',
    ],
    specializations: [
      'General Psychiatric Nursing',
      'Child Psychiatric Nursing',
      'Geriatric Psychiatric Nursing',
      'Community Psychiatric Nursing',
      'Psychiatric Rehabilitation Nursing',
      'Psychiatric Emergency Nursing',
      'Psychiatric Intensive Care Nursing',
      'Psychiatric Research Nursing',
      'Psychiatric Education Nursing',
      'Psychiatric Administration Nursing',
      'Psychiatric Consultation Nursing',
      'Psychiatric Crisis Intervention Nursing',
      'Psychiatric Forensic Nursing',
      'Psychiatric Substance Abuse Nursing',
      'Psychiatric Trauma Nursing',
    ],
    experienceOptions: [
      '0-2 years',
      '2-5 years',
      '5-10 years',
      '10-15 years',
      '15-20 years',
      '20+ years',
    ],
    bioPlaceholder: 'Describe your nursing approach, patient care philosophy, and areas of expertise in psychiatric nursing...',
  },
  {
    value: 'psychiatric_social_worker',
    label: 'Psychiatric Social Worker',
    description: 'Social worker specializing in mental health support and rehabilitation',
    icon: <PsychologyIcon />,
    qualifications: [
      'MSW (Psychiatric Social Work)',
      'MA Social Work',
      'MSc Social Work',
      'MA Psychiatric Social Work',
      'MPhil Psychiatric Social Work',
      'PhD Social Work',
      'Diploma in Psychiatric Social Work',
      'Advanced Diploma in Psychiatric Social Work',
      'Post Graduate Diploma in Psychiatric Social Work',
      'Certificate in Psychiatric Social Work',
      'M.Phil Social Work',
      'MA Clinical Social Work',
      'MSc Clinical Social Work',
      'M.Phil Clinical Social Work',
      'Specialized Training in Psychiatric Social Work',
    ],
    specializations: [
      'Clinical Social Work',
      'Community Mental Health',
      'Psychiatric Rehabilitation',
      'Family Therapy',
      'Crisis Intervention',
      'Substance Abuse Counseling',
      'Trauma Counseling',
      'Child & Adolescent Social Work',
      'Geriatric Social Work',
      'Forensic Social Work',
      'Medical Social Work',
      'School Social Work',
      'Military Social Work',
      'Research Social Work',
      'Administrative Social Work',
    ],
    experienceOptions: [
      '0-2 years',
      '2-5 years',
      '5-10 years',
      '10-15 years',
      '15-20 years',
      '20+ years',
    ],
    bioPlaceholder: 'Describe your social work approach, intervention strategies, and areas of expertise in psychiatric social work...',
  },
];

const languageOptions = [
  'English',
  'Hindi',
  'Bengali',
  'Telugu',
  'Marathi',
  'Tamil',
  'Urdu',
  'Gujarati',
  'Kannada',
  'Malayalam',
  'Punjabi',
  'Odia',
  'Assamese',
  'Kashmiri',
  'Sanskrit',
];

const experienceOptions = [
  '0-2 years',
  '2-5 years',
  '5-10 years',
  '10-15 years',
  '15-20 years',
  '20+ years',
];

// Add keyframes for animation
const keyframes = `
  @keyframes fadeIn {
    0% {
      opacity: 0;
      transform: scale(0.8);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const MultiSelect = ({ label, value, onChange, options, icon, error, helperText, required, disabled }) => {
  const theme = useTheme();
  
  return (
    <FormControl fullWidth error={!!error} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value || []}
        onChange={onChange}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((value) => (
              <Chip
                key={value}
                label={value}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  margin: '2px',
                  height: 32,
                  borderRadius: 16,
                  border: `2px solid ${theme.palette.primary.main}`,
                  transition: 'all 0.2s ease-in-out',
                  animation: 'fadeIn 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.3)}`,
                  },
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.primary.contrastText,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: theme.palette.error.light,
                      transform: 'scale(1.1)',
                    },
                  },
                }}
                onDelete={() => {
                  onChange({
                    target: {
                      value: value.filter((item) => item !== value),
                    },
                  });
                }}
              />
            ))}
          </Box>
        )}
        MenuProps={MenuProps}
        startAdornment={
          <InputAdornment position="start">
            {icon}
          </InputAdornment>
        }
      >
        {options.map((option) => (
          <MenuItem 
            key={option} 
            value={option}
            sx={{
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.16),
                },
              },
            }}
          >
            <Checkbox 
              checked={value?.indexOf(option) > -1}
              sx={{
                color: alpha(theme.palette.primary.main, 0.5),
                '&.Mui-checked': {
                  color: theme.palette.primary.main,
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            />
            <ListItemText 
              primary={option}
              primaryTypographyProps={{
                sx: {
                  fontWeight: value?.indexOf(option) > -1 ? 600 : 400,
                },
              }}
            />
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText error>{error}</FormHelperText>}
      {helperText && !error && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

const getRegistrationFields = (professionalType) => {
  const fields = {
    psychiatrist: {
      label: 'MCI Registration Number',
      helperText: 'Enter your Medical Council of India registration number',
      placeholder: 'MCI-XXXXX',
      required: true,
      dateLabel: 'Registration Date',
      dateHelperText: 'Date when you received your MCI registration',
      dateValidation: {
        min: new Date(new Date().setFullYear(new Date().getFullYear() - 50)),
        max: new Date()
      },
      authority: 'Medical Council of India (MCI)'
    },
    clinical_psychologist: {
      label: 'RCI Registration Number',
      helperText: 'Enter your Rehabilitation Council of India registration number',
      placeholder: 'RCI-XXXXX',
      required: true,
      dateLabel: 'Registration Date',
      dateHelperText: 'Date when you received your RCI registration',
      dateValidation: {
        min: new Date(new Date().setFullYear(new Date().getFullYear() - 50)),
        max: new Date()
      },
      authority: 'Rehabilitation Council of India (RCI)'
    },
    counselor: {
      label: 'Professional Registration Number',
      helperText: 'Enter your professional registration number',
      placeholder: 'REG-XXXXX',
      required: true,
      dateLabel: 'Registration Date',
      dateHelperText: 'Date when you received your professional registration',
      dateValidation: {
        min: new Date(new Date().setFullYear(new Date().getFullYear() - 50)),
        max: new Date()
      },
      authority: 'Professional Counseling Association'
    },
    psychiatric_nurse: {
      label: 'Nursing Council Registration Number',
      helperText: 'Enter your Nursing Council registration number',
      placeholder: 'NC-XXXXX',
      required: true,
      dateLabel: 'Registration Date',
      dateHelperText: 'Date when you received your Nursing Council registration',
      dateValidation: {
        min: new Date(new Date().setFullYear(new Date().getFullYear() - 50)),
        max: new Date()
      },
      authority: 'Indian Nursing Council'
    },
    psychiatric_social_worker: {
      label: 'Social Work Registration Number',
      helperText: 'Enter your Social Work registration number',
      placeholder: 'SW-XXXXX',
      required: true,
      dateLabel: 'Registration Date',
      dateHelperText: 'Date when you received your Social Work registration',
      dateValidation: {
        min: new Date(new Date().setFullYear(new Date().getFullYear() - 50)),
        max: new Date()
      },
      authority: 'Professional Social Work Association'
    }
  };

  return fields[professionalType] || null;
};

const getLicenseFields = (professionalType) => {
  const fields = {
    psychiatrist: {
      label: 'Medical License Number',
      helperText: 'Enter your medical license number',
      placeholder: 'ML-XXXXX',
      required: true
    },
    clinical_psychologist: {
      label: 'Clinical License Number',
      helperText: 'Enter your clinical psychology license number',
      placeholder: 'CL-XXXXX',
      required: true
    },
    counselor: {
      label: 'Counseling License Number',
      helperText: 'Enter your counseling license number',
      placeholder: 'COUN-XXXXX',
      required: true
    },
    psychiatric_nurse: {
      label: 'Nursing License Number',
      helperText: 'Enter your nursing license number',
      placeholder: 'NL-XXXXX',
      required: true
    },
    psychiatric_social_worker: {
      label: 'Social Work License Number',
      helperText: 'Enter your social work license number',
      placeholder: 'SWL-XXXXX',
      required: true
    }
  };

  return fields[professionalType] || null;
};

const ProfessionalDetails = ({ formData = {}, onChange, errors = {} }) => {
  const theme = useTheme();
  const [localErrors, setLocalErrors] = useState({});
  const [selectedDoctorType, setSelectedDoctorType] = useState(formData.professionalType || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (formData.professionalType) {
      setSelectedDoctorType(formData.professionalType);
    }
  }, [formData.professionalType]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    onChange({ [field]: value });
    
    // Validate field in real-time using Zod schema
    const error = validateField(professionalDetailsSchema, field, value);
    setLocalErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const handleMultiSelectChange = (field) => (event) => {
    const value = event.target.value;
    onChange({ [field]: value });
    
    // Validate field in real-time using Zod schema
    const error = validateField(professionalDetailsSchema, field, value);
    setLocalErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const handleDoctorTypeChange = (event) => {
    const type = event.target.value;
    setSelectedDoctorType(type);
    setLocalErrors(prev => ({ ...prev, professionalType: null }));
    
    // Reset related fields when doctor type changes
    onChange({ 
      professionalType: type,
      specializations: [],
      qualifications: [],
      experience: '',
      languages: [],
      bio: '',
      registrationNumber: '',
      registrationDate: null,
      registrationAuthority: '',
      licenseNumber: '',
      licenseExpiryDate: null,
      additionalCertifications: [],
    });
  };

  const handleDateChange = (field) => (date) => {
    onChange({ [field]: date });
    
    // Validate field in real-time using Zod schema
    const error = validateField(professionalDetailsSchema, field, date);
    setLocalErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  // Combine server errors with local validation errors
  const combinedErrors = { ...errors, ...localErrors };

  const getDoctorTypeDescription = () => {
    const type = doctorTypes.find(t => t.value === selectedDoctorType);
    return type ? type.description : '';
  };

  const getSelectedType = () => {
    return doctorTypes.find(t => t.value === selectedDoctorType);
  };

  const getExperienceOptions = () => {
    return getSelectedType()?.experienceOptions || experienceOptions;
  };

  const getBioPlaceholder = () => {
    return getSelectedType()?.bioPlaceholder || 'Write a brief description about your professional background...';
  };

  const renderRegistrationFields = () => {
    const fields = getRegistrationFields(selectedDoctorType);
    if (!fields) return null;

    return (
      <Grid item xs={12}>
        <StyledPaper>
          <Typography variant="subtitle1" gutterBottom color="primary">
            Professional Registration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your professional registration helps us ensure the highest standards of mental health care.
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label={fields.label}
                value={formData.registrationNumber || ''}
                onChange={handleChange('registrationNumber')}
                error={!!combinedErrors.registrationNumber}
                helperText={combinedErrors.registrationNumber || fields.helperText}
                required={fields.required}
                placeholder={fields.placeholder}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={fields.dateLabel}
                  value={formData.registrationDate ? new Date(formData.registrationDate) : null}
                  onChange={handleDateChange('registrationDate')}
                  minDate={fields.dateValidation.min}
                  maxDate={fields.dateValidation.max}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      fullWidth
                      error={!!combinedErrors.registrationDate}
                      helperText={combinedErrors.registrationDate || fields.dateHelperText}
                      required={fields.required}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarTodayIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Registration Authority"
                value={formData.registrationAuthority || fields.authority}
                onChange={handleChange('registrationAuthority')}
                error={!!combinedErrors.registrationAuthority}
                helperText={combinedErrors.registrationAuthority}
                required={fields.required}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VerifiedIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </StyledPaper>
      </Grid>
    );
  };

  const renderLicenseFields = () => {
    const fields = getLicenseFields(selectedDoctorType);
    if (!fields) return null;

    return (
      <Grid item xs={12}>
        <StyledPaper>
          <Typography variant="subtitle1" gutterBottom color="primary">
            Professional License
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your professional license demonstrates your commitment to providing quality mental health care.
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label={fields.label}
                value={formData.licenseNumber || ''}
                onChange={handleChange('licenseNumber')}
                error={!!combinedErrors.licenseNumber}
                helperText={combinedErrors.licenseNumber || fields.helperText}
                required={fields.required}
                placeholder={fields.placeholder}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VerifiedIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {formData.licenseNumber && (
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="License Expiry Date"
                    value={formData.licenseExpiryDate ? new Date(formData.licenseExpiryDate) : null}
                    onChange={handleDateChange('licenseExpiryDate')}
                    minDate={new Date()}
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        fullWidth
                        error={!!combinedErrors.licenseExpiryDate}
                        helperText={combinedErrors.licenseExpiryDate || 'Please enter the expiry date of your license'}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarTodayIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            )}
          </Grid>
        </StyledPaper>
      </Grid>
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
        Professional Information
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        We're here to help you showcase your expertise and qualifications. Your information helps us ensure the highest standards of mental health care for our community.
      </Typography>

      {showSuccess && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          onClose={() => setShowSuccess(false)}
        >
          Your professional details have been saved successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="subtitle1" gutterBottom color="primary">
              Professional Type
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <StyledFormControl 
                  fullWidth 
                  error={!!combinedErrors.professionalType}
                  disabled={isSubmitting}
                >
                  <InputLabel>Select Your Professional Type</InputLabel>
                  <StyledSelect
                    value={selectedDoctorType}
                    label="Select Your Professional Type"
                    onChange={handleDoctorTypeChange}
                    required
                    startAdornment={
                      <InputAdornment position="start">
                        <PsychologyIcon color="primary" />
                      </InputAdornment>
                    }
                  >
                    {doctorTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {type.icon}
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </StyledSelect>
                  {combinedErrors.professionalType && (
                    <FormHelperText error>
                      {combinedErrors.professionalType}
                    </FormHelperText>
                  )}
                </StyledFormControl>
              </Grid>

              {selectedDoctorType && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      {getDoctorTypeDescription()}
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </StyledPaper>
        </Grid>

        {selectedDoctorType && (
          <>
            {renderRegistrationFields()}
            {renderLicenseFields()}

            <Grid item xs={12}>
              <StyledPaper>
                <Typography variant="subtitle1" gutterBottom color="primary">
                  Specializations & Qualifications
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <MultiSelect
                      label="Specializations"
                      value={formData.specializations || []}
                      onChange={handleMultiSelectChange('specializations')}
                      options={getSelectedType()?.specializations || []}
                      icon={<VerifiedIcon color="primary" />}
                      error={!!combinedErrors.specializations}
                      helperText={combinedErrors.specializations || 'Please select at least one specialization'}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MultiSelect
                      label="Qualifications"
                      value={formData.qualifications || []}
                      onChange={handleMultiSelectChange('qualifications')}
                      options={getSelectedType()?.qualifications || []}
                      icon={<SchoolIcon color="primary" />}
                      error={!!combinedErrors.qualifications}
                      helperText={combinedErrors.qualifications || 'Please select at least one qualification'}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                </Grid>
              </StyledPaper>
            </Grid>

            <Grid item xs={12}>
              <StyledPaper>
                <Typography variant="subtitle1" gutterBottom color="primary">
                  Experience & Languages
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <StyledFormControl 
                      fullWidth 
                      error={!!combinedErrors.experience}
                      disabled={isSubmitting}
                    >
                      <InputLabel>Years of Experience</InputLabel>
                      <StyledSelect
                        value={formData.experience || ''}
                        label="Years of Experience"
                        onChange={handleChange('experience')}
                        required
                        startAdornment={
                          <InputAdornment position="start">
                            <WorkIcon color="primary" />
                          </InputAdornment>
                        }
                      >
                        {getExperienceOptions().map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </StyledSelect>
                      {combinedErrors.experience && (
                        <FormHelperText error>
                          {combinedErrors.experience}
                        </FormHelperText>
                      )}
                    </StyledFormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <MultiSelect
                      label="Languages Spoken"
                      value={formData.languages || []}
                      onChange={handleMultiSelectChange('languages')}
                      options={languageOptions}
                      icon={<TranslateIcon color="primary" />}
                      error={!!combinedErrors.languages}
                      helperText={combinedErrors.languages || 'Please select at least one language'}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                </Grid>
              </StyledPaper>
            </Grid>

            <Grid item xs={12}>
              <StyledPaper>
                <Typography variant="subtitle1" gutterBottom color="primary">
                  Professional Bio
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      multiline
                      rows={4}
                      placeholder={getBioPlaceholder()}
                      value={formData.bio || ''}
                      onChange={handleChange('bio')}
                      error={!!combinedErrors.bio}
                      helperText={combinedErrors.bio || 'This information will be displayed on your public profile'}
                      required
                      disabled={isSubmitting}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DescriptionIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </StyledPaper>
            </Grid>
          </>
        )}
      </Grid>

      <Fade in timeout={500}>
        <Box sx={{ mt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Your professional information helps us:
            </Typography>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>Ensure the highest standards of mental health care</li>
              <li>Connect you with patients who match your expertise</li>
              <li>Maintain professional and ethical standards</li>
              <li>Provide you with relevant resources and support</li>
            </ul>
          </Alert>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              Need assistance?
            </Typography>
            <Tooltip title="Our support team is here to help you">
              <IconButton size="small" color="primary">
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Fade>

      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ mt: 3, fontStyle: 'italic' }}
      >
        * Required fields are marked with an asterisk. We carefully verify all information to ensure the highest standards of mental health care.
      </Typography>
    </Box>
  );
};

export default ProfessionalDetails; 